<template>
    <QuestionBox :title="step.question" :text="$t('module.orderImagesText')" />
    <div class="m-dropImages" v-if="itemsLeft.length || itemsRight.length">
        <div class="m-dropImages__left" ref="sourceEl">
            <div v-for="item in itemsLeft" :key="item.id" class="m-dropImages__spot u-posRelative u-br u-borderDashed" v-drag="{ dragData: item, onDragStart: onDragStart }">
                <img 
                    v-if="item.src"
                    :src="item.src" 
                    alt="Image"
                    class="m-dropImages__image u-posAbsolute u-br u-cover"
                />
            </div>
        </div>

        <div class="m-dropImages__right" ref="targetEl">
			<div 
                v-for="(item, index) in itemsRight" 
                :key="item.id" 
                class="m-dropImages__spot u-posRelative u-br u-borderDashed" 
                v-drop="{ dragData: item, onDragEnter: onDragEnter, onDrop: onDrop, onDragStart: onDragStart }"
                :class="{
                    'u-borderGreen': matchedIndexes.matchingIndexes?.includes(index),
                    'u-borderRed': matchedIndexes.nonMatchingIndexes?.includes(index),
                }"
            >
                <div class="m-dropImages__imageNumber a-circle -small -tertiary">{{ index + 1 }}</div>
                <img 
                    v-if="item.src"
                    :src="item.src" 
                    alt="Image"
                    class="m-dropImages__image u-posAbsolute u-br u-cover"
                />
            </div>
		</div>
    </div>
    <FixedButton 
        :isDisabled="!allItemsFilled" 
        @click="handleButtonClick" 
        :buttonClass="showTryAgain ? '-reset' : ''">
            {{ showTryAgain ? $t('module.tryAgain') : (answeredCorrect ? $t('module.nextQuestion') : $t('module.checkQuestion')) }}
    </FixedButton>
</template>

<script setup>
import { inject, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDraggable } from 'vue-draggable-plus';
import { shuffleArray, addAttempt } from '../../utils/globalFunctions';
import 'drag-drop-touch';

// Components
import QuestionBox from './QuestionBox.vue';
import FixedButton from './FixedButton.vue';

const props = defineProps({
    step: Object,
});

const { t } = useI18n();
const shuffledItems = shuffleArray([...props.step.images]);
const totalItems = shuffledItems.map((src, index) => ({ id: index, src }));
const initialItemsRight = totalItems.map(({ id }) => ({ id, src: '' }));
const initialItemsLeft = totalItems;
const itemsLeft = ref(initialItemsLeft);
const itemsRight = ref(initialItemsRight);
const showTryAgain = ref(false);
const answeredCorrect = ref(false);
const sourceEl = ref(null);
const targetEl = ref(null);
const dragging = ref(null);
const matchedIndexes = ref({ matchingIndexes: [], nonMatchingIndexes: [] });

const completeStep = inject('completeStep');
const showNotice = inject('showNotice');
const hideNotice = inject('hideNotice');

const attempts = defineModel( 'attempts' );

const findTargetIndex = (array, id) => {
    return array.value.findIndex( item => item.id === id );
};

const draggableTarget = useDraggable(targetEl, itemsRight, {
    animation: 150,
});

const onDragStart = (domEl, dragEvent, dragOptions, options) => {
    dragging.value = {domEl: domEl, dragData: dragOptions.dragData}
};

const onDragEnter = (domEl, dragEvent, dragOptions, options) => {
    let dragIndex = 0
    if(dragging.value){
        dragIndex = dragging.value;
    }
    const dropIndex = dragOptions.dragData ?? 0
    return dragIndex !== dropIndex
};

const hasParentWithClass = (element, className) => {
    while (element) {
        if (element.classList && element.classList.contains(className)) {
            return true;
        }
        element = element.parentElement;
    }
    return false;
};

const onDrop = (domEl, dragEvent, dragOptions, options) => {
    if(dragging.value?.domEl && hasParentWithClass(dragging.value.domEl, 'm-dropImages__left')) {
        const findIndexLeft = findTargetIndex(itemsLeft, dragging.value.dragData.id);
        const findIndexRight = findTargetIndex(itemsRight, dragOptions.dragData.id );

        const temp = dragging.value.dragData.src;
        
        itemsLeft.value[findIndexLeft].src = itemsRight.value[findIndexRight].src;
        itemsRight.value[findIndexRight].src = temp;
    }
};

const isOrderMatching = (originalArray, currentArray) => {
    const matchingIndexes = [];
    const nonMatchingIndexes = [];

    originalArray.forEach((src, index) => {
        if (src === currentArray[index].src) {
            matchingIndexes.push(index);
        } else {
            nonMatchingIndexes.push(index);
        }
    });

    return { matchingIndexes, nonMatchingIndexes };
};

const allItemsFilled = computed(() => {
    return itemsRight.value.every(item => item.src !== '');
});

const checkAnswer = () => {
    sourceEl.value.classList.add('-disableDrag');
    targetEl.value.classList.add('-disableDrag');

    matchedIndexes.value = isOrderMatching(props.step.images, itemsRight.value);

    if (matchedIndexes.value.nonMatchingIndexes.length === 0) {

        attempts.value.push( addAttempt( props.step.uuid, true ) );

        answeredCorrect.value = true;
        const textCorrect = props.step.feedback_correct ? props.step.feedback_correct : t('module.correctOrderText');
        showNotice(t('module.correctOrderTitle'), textCorrect, '-success');
        completeStep(props.step.uuid);
    } else {

        attempts.value.push( addAttempt( props.step.uuid, false ) );

        showNotice(t('module.wrongOrderTitle'), t('module.wrongOrderText'), '-error');
        showTryAgain.value = true; 
    }
};

const resetQuestion = () => {
    sourceEl.value.classList.remove('-disableDrag');
    targetEl.value.classList.remove('-disableDrag');

    itemsLeft.value.forEach((item, index) => {
        item.src = shuffledItems[index];
    });

    itemsRight.value.forEach(item => item.src = '');

    matchedIndexes.value = { matchingIndexes: [], nonMatchingIndexes: [] };
    showTryAgain.value = false;
    hideNotice();
};

const handleButtonClick = () => {
    if (showTryAgain.value) {
        resetQuestion();
    } else if (answeredCorrect.value) {
        completeStep(props.step.uuid, true, false);
        hideNotice();
    } else {
        checkAnswer();
    }
};
</script>
